import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useSubscription } from '../hooks/useSubscription';
import { getApiUrl, getAuthHeaders } from '../config/api';
import axios from 'axios';
import { 
  Plus, 
  Calendar, 
  MapPin, 
  Users, 
  Camera, 
  Video, 
  Edit3, 
  Trash2, 
  Upload,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

export default function Media() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [eventThumbnails, setEventThumbnails] = useState({});
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    description: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasFeature } = useSubscription();

  // Fetch events
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${getApiUrl('/api/rodeos')}`, {
        headers: getAuthHeaders()
      });
      
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      showError('Error loading events');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch media for an event
  const fetchMedia = async (eventId) => {
    try {
      setIsLoadingMedia(true);
      const response = await axios.get(`${getApiUrl('/api/rodeos')}/${eventId}/media`, {
        headers: getAuthHeaders()
      });
      
      setMedia(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
      showError('Error loading media');
    } finally {
      setIsLoadingMedia(false);
    }
  };

  // Create event
  const createEvent = async () => {
    if (!formData.name.trim()) {
      showError('Event name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.post(`${getApiUrl('/api/rodeos')}`, formData, {
        headers: getAuthHeaders()
      });

      showSuccess('Event created successfully!');
      setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
      setShowCreateModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      showError(error.response?.data?.error || 'Error creating event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update event
  const updateEvent = async () => {
    if (!editingEvent || !formData.name.trim()) {
      showError('Event name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.put(`${getApiUrl('/api/rodeos')}/${editingEvent.id}`, formData, {
        headers: getAuthHeaders()
      });

      showSuccess('Event updated successfully!');
      setShowEditModal(false);
      setEditingEvent(null);
      setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      showError(error.response?.data?.error || 'Error updating event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete event
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all associated media.')) {
      return;
    }

    try {
      await axios.delete(`${getApiUrl('/api/rodeos')}/${eventId}`, {
        headers: getAuthHeaders()
      });

      showSuccess('Event deleted successfully!');
      fetchEvents();
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
        setMedia([]);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showError(error.response?.data?.error || 'Error deleting event');
    }
  };

  // Generate thumbnail for images
  const generateThumbnail = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate thumbnail dimensions (max 300x300)
          const maxSize = 300;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              console.error('Failed to generate thumbnail blob');
              resolve(null);
            }
          }, 'image/jpeg', 0.8);
        } catch (error) {
          console.error('Error generating thumbnail:', error);
          resolve(null);
        }
      };
      
      img.onerror = () => {
        console.error('Error loading image for thumbnail generation');
        resolve(null);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!selectedEvent) return;

    try {
      setUploadingFiles(true);
      const formData = new FormData();
      
      // Process each file
      for (const file of Array.from(files)) {
        formData.append('media', file);
        
        // Generate thumbnail for images
        if (file.type.startsWith('image/')) {
          const thumbnail = await generateThumbnail(file);
          if (thumbnail) {
            formData.append('thumbnail', thumbnail, `thumb_${file.name}`);
          }
        }
      }

      const response = await axios.post(`${getApiUrl('/api/rodeos')}/${selectedEvent.id}/media`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      showSuccess('Media uploaded successfully!');
      fetchMedia(selectedEvent.id);
    } catch (error) {
      console.error('Error uploading media:', error);
      showError('Error uploading media');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'Invalid date';
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!selectedEvent) return;
    
    try {
      await axios.delete(`${getApiUrl('/api/rodeos')}/${selectedEvent.id}/media/${mediaId}`, {
        headers: getAuthHeaders()
      });
      
      showSuccess('Media deleted successfully!');
      fetchMedia(selectedEvent.id);
    } catch (error) {
      console.error('Error deleting media:', error);
      showError(error.response?.data?.error || 'Error deleting media');
    }
  };

  // Initialize
  useEffect(() => {
    fetchEvents();
  }, []);

  // After events load, fetch up to two thumbnails per event
  useEffect(() => {
    const fetchThumbnailsForEvents = async () => {
      try {
        const idsToFetch = (events || []).filter(e => e && e.id && !eventThumbnails[e.id]).map(e => e.id);
        await Promise.all(idsToFetch.map(async (eventId) => {
          try {
            const response = await axios.get(`${getApiUrl('/api/rodeos')}/${eventId}/media`, {
              headers: getAuthHeaders()
            });
            const firstTwo = Array.isArray(response.data) ? response.data.slice(0, 2) : [];
            setEventThumbnails(prev => ({ ...prev, [eventId]: firstTwo }));
          } catch (err) {
            // Silent fail per event
          }
        }));
      } catch (e) {
        // Silent fail overall
      }
    };
    if (events && events.length > 0) {
      fetchThumbnailsForEvents();
    }
  }, [events]);

  // Load media when event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchMedia(selectedEvent.id);
    }
  }, [selectedEvent]);

  // Check if user has access to Media feature
  if (!hasFeature('rodeos')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-6xl mb-4">📸</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Media Feature</h2>
            <p className="text-gray-600 mb-6">
              Create and manage events, capture memories with photos and videos, and organize your experiences.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Camera className="mr-2 h-6 w-6 sm:h-8 sm:w-8 text-black" />
              Media
            </h1>
            <p className="text-gray-600 mt-2">Create and manage your events, capture memories with photos and videos</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Event
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">📸</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No events yet</h3>
            <p className="text-gray-600 mb-6">Create your first event to start capturing memories</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Event
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 truncate">{event.name}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingEvent(event);
                          setFormData({
                            name: event.name,
                            location: event.location || '',
                            startDate: event.start_date,
                            endDate: event.end_date || '',
                            description: event.description || ''
                          });
                          setShowEditModal(true);
                        }}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit event"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteEvent(event.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete event"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {event.location && (
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="text-sm">{event.location}</span>
                      </div>
                    )}
                    {event.start_date && (
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span className="text-sm">{formatDate(event.start_date)}</span>
                        {event.end_date && event.end_date !== event.start_date && (
                          <span className="text-sm ml-1">- {formatDate(event.end_date)}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <Camera className="w-4 h-4 mr-2" />
                      <span className="text-sm">{event.media_count || 0} photos/videos</span>
                    </div>
                  </div>
                  
                  {event.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowMediaModal(true);
                      }}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      View Media
                    </button>
                    <button
                      onClick={() => setSelectedEvent(event)}
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Manage
                    </button>
                  </div>

                  {/* Small thumbnails preview */}
                  {Array.isArray(eventThumbnails[event.id]) && eventThumbnails[event.id].length > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      {eventThumbnails[event.id].map((item) => (
                        <div key={item.id} className="w-10 h-10 rounded overflow-hidden bg-gray-100">
                          {item.file_type === 'image' ? (
                            <img src={item.url} alt={item.file_name} className="w-full h-full object-cover" />
                          ) : (
                            <video src={item.url} className="w-full h-full object-cover" muted />
                          )}
                        </div>
                      ))}
                      {(event.media_count || 0) > 2 && (
                        <span className="text-gray-400 text-sm">...</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Event Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Create Event</h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={(e) => { e.preventDefault(); createEvent(); }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter event name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter location"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={formData.endDate}
                          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                        placeholder="Enter event description"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Event'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Event Modal */}
        {showEditModal && editingEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Edit Event</h2>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingEvent(null);
                      setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={(e) => { e.preventDefault(); updateEvent(); }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter event name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter location"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={formData.endDate}
                          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                        placeholder="Enter event description"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingEvent(null);
                        setFormData({ name: '', location: '', startDate: '', endDate: '', description: '' });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Updating...' : 'Update Event'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Media Management Modal */}
        {selectedEvent && !showMediaModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold">{selectedEvent.name}</h2>
                    {selectedEvent.start_date && (
                      <p className="text-gray-600">{formatDate(selectedEvent.start_date)}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowMediaModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Camera className="w-4 h-4" />
                      View Media
                    </button>
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Photos/Videos</label>
                  
                  {/* Upload Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {/* Camera Capture - Photo */}
                    <button
                      onClick={() => document.getElementById('camera-photo').click()}
                      disabled={uploadingFiles}
                      className="flex items-center justify-center space-x-2 p-3 border-2 border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      <Camera className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">Take Photo</span>
                    </button>
                    
                    {/* Camera Capture - Video */}
                    <button
                      onClick={() => document.getElementById('camera-video').click()}
                      disabled={uploadingFiles}
                      className="flex items-center justify-center space-x-2 p-3 border-2 border-green-300 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
                    >
                      <Video className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Record Video</span>
                    </button>
                    
                    {/* File Upload */}
                    <button
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={uploadingFiles}
                      className="flex items-center justify-center space-x-2 p-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Upload className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Upload Files</span>
                    </button>
                  </div>
                  
                  {/* Hidden Input Elements */}
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="file-upload"
                    disabled={uploadingFiles}
                  />
                  
                  {/* Camera Photo Input */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="camera-photo"
                    disabled={uploadingFiles}
                  />
                  
                  {/* Camera Video Input */}
                  <input
                    type="file"
                    accept="video/*"
                    capture="environment"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="camera-video"
                    disabled={uploadingFiles}
                  />
                  
                  {/* Upload Status */}
                  {uploadingFiles && (
                    <div className="text-center py-2">
                      <div className="inline-flex items-center space-x-2 text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Media Grid */}
                {isLoadingMedia ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : media.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">📷</div>
                    <p className="text-gray-600">No media uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {media.map((item, index) => (
                      <div key={item.id} className="relative group">
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          {item.file_type === 'image' ? (
                            <img
                              src={item.url}
                              alt={item.file_name}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => {
                                setSelectedMedia(item);
                                setMediaIndex(index);
                                setShowMediaModal(true);
                              }}
                            />
                          ) : (
                            <video
                              src={item.url}
                              className="w-full h-full object-cover cursor-pointer"
                              onClick={() => {
                                setSelectedMedia(item);
                                setMediaIndex(index);
                                setShowMediaModal(true);
                              }}
                            />
                          )}
                        </div>
                        
                        {/* Delete button - appears on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this media?')) {
                              deleteMedia(item.id);
                            }
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="Delete media"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Media Viewer Modal */}
        {showMediaModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[70]">
            <div className="relative max-w-6xl max-h-[90vh] w-full">
              <button
                onClick={() => setShowMediaModal(false)}
                className="absolute top-4 right-4 z-10 text-white hover:text-gray-300"
              >
                <X className="w-8 h-8" />
              </button>
              
              {media.length > 0 && (
                <>
                  <div className="flex justify-center items-center h-full">
                    {media[mediaIndex]?.file_type === 'image' ? (
                      <img
                        src={media[mediaIndex]?.url}
                        alt={media[mediaIndex]?.file_name}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <video
                        src={media[mediaIndex]?.url}
                        className="max-w-full max-h-full"
                        controls
                        autoPlay
                      />
                    )}
                  </div>
                  
                  {media.length > 1 && (
                    <>
                      <button
                        onClick={() => setMediaIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1))}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                      >
                        <ChevronLeft className="w-8 h-8" />
                      </button>
                      <button
                        onClick={() => setMediaIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0))}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                      >
                        <ChevronRight className="w-8 h-8" />
                      </button>
                    </>
                  )}
                  
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
                    {mediaIndex + 1} of {media.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
